import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import type { Verdict } from "../../studio/studioTypes"
import type { ApprovedSnapshot } from "./approvedSnapshot"

const VERDICT_KEY: Record<Verdict, TranslationKeys> = {
  compliant: "MC_DECL_V2_VERDICT_COMPLIANT",
  compliant_with_conditions: "MC_DECL_V2_VERDICT_COMPLIANT_WITH_CONDITIONS",
  non_compliant: "MC_DECL_V2_VERDICT_NON_COMPLIANT",
  pending: "MC_DECL_V2_VERDICT_PENDING",
  unknown: "MC_DECL_V2_VERDICT_UNKNOWN",
}

/** Exported for the "one verdict, one appearance" test, which pins
 *  `compliant_with_conditions` to the same token `verdictDisplay` uses. The
 *  class stays a literal because Tailwind only emits utilities it can read
 *  from source. */
export const VERDICT_MATRIX_DOT_CLASS: Record<Verdict, string> = {
  compliant: "bg-success",
  compliant_with_conditions: "bg-attention",
  non_compliant: "bg-red1",
  pending: "bg-grey5",
  unknown: "bg-grey5",
}

const DOT: Record<Verdict, string> = VERDICT_MATRIX_DOT_CLASS

function VerdictDot({
  verdict,
  className,
}: {
  verdict: Verdict | null
  className?: string
}) {
  return (
    <span
      data-verdict={verdict ?? "none"}
      className={cn(
        "inline-block h-2 w-2 shrink-0 rounded-full",
        verdict ? DOT[verdict] : "border border-grey4 bg-transparent",
        className,
      )}
    />
  )
}

/** One dot per Verordnung with its overall verdict: the collapsed summary
 *  line of the detail section. */
export function ApprovedVerdictSummary({
  snapshot,
}: {
  snapshot: ApprovedSnapshot
}) {
  const { translate } = useTranslation()
  return (
    <div
      data-testid="approved-verdict-summary"
      className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-muted-foreground"
    >
      {snapshot.frameworks.map((fw) => (
        <span key={fw.key} className="inline-flex items-center gap-1.5">
          <VerdictDot verdict={fw.summary} />
          <span className="text-foreground">{fw.name}</span>
          <span>
            {fw.summary
              ? translate(VERDICT_KEY[fw.summary])
              : translate("MC_DECL_V2_APPROVED_MATRIX_NONE")}
          </span>
        </span>
      ))}
    </div>
  )
}

/** Articles x Verordnungen, one dot per cell. Columns grow with the
 *  declaration's Verordnungen and scroll horizontally inside the dialog. */
export function ApprovedVerdictMatrix({
  snapshot,
}: {
  snapshot: ApprovedSnapshot
}) {
  const { translate } = useTranslation()
  const cellLabel = (fwName: string, v: Verdict | null) =>
    `${fwName} · ${
      v
        ? translate(VERDICT_KEY[v])
        : translate("MC_DECL_V2_APPROVED_MATRIX_NONE")
    }`
  return (
    <TooltipProvider delayDuration={200}>
      <div
        data-testid="approved-verdict-matrix"
        className="max-h-[40vh] w-full overflow-auto rounded-md border border-grey4"
      >
        <table className="w-full border-collapse text-xs">
          <thead className="sticky top-0 bg-card">
            <tr>
              <th className="px-3 py-2 text-left font-normal uppercase tracking-wide text-muted-foreground">
                {translate("MC_DECL_V2_APPROVED_MATRIX_ARTICLE")}
              </th>
              {snapshot.frameworks.map((fw) => (
                <th
                  key={fw.key}
                  className="whitespace-nowrap px-3 py-2 text-center font-normal text-foreground"
                >
                  {fw.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {snapshot.articles.map((a) => (
              <tr key={a.number} className="border-t border-grey4">
                <td className="max-w-64 px-3 py-1.5 text-left">
                  {/* Flex row with `min-w-0` so `truncate` actually applies to
                      the name (Copilot: inline text never ellipsizes). */}
                  <div className="flex min-w-0 items-baseline gap-2">
                    <span className="font-mono shrink-0 text-[11px] text-muted-foreground">
                      {a.number}
                    </span>
                    {a.supplierCode && a.supplierCode !== a.number && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="font-mono shrink-0 text-[11px] text-grey5">
                            · {a.supplierCode}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          {translate("MC_DECL_SB_SUPPLIER_CODE")}
                        </TooltipContent>
                      </Tooltip>
                    )}
                    <span className="min-w-0 truncate text-foreground">
                      {a.name}
                    </span>
                  </div>
                </td>
                {snapshot.frameworks.map((fw) => {
                  const v = fw.byArticle[a.number] ?? null
                  return (
                    <td key={fw.key} className="px-3 py-1.5 text-center">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          {/* Focusable for the tooltip, so it needs a name
                              matching the tooltip text (Copilot, #2036). */}
                          <span
                            tabIndex={0}
                            aria-label={cellLabel(fw.name, v)}
                            className="inline-flex h-5 w-5 items-center justify-center rounded"
                          >
                            <VerdictDot verdict={v} />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          {cellLabel(fw.name, v)}
                        </TooltipContent>
                      </Tooltip>
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </TooltipProvider>
  )
}
